import client from "./client";
import { Edition, EditionHit } from "@/types/edition";
import { Article } from "@/types/article";
import { EndUser, Subscriptions } from "@/types/user";
import { AppSettings } from "@/types/appSettings";
import { Faq } from "@/types/faq";
import { AppEventData } from "@/types/appEvents";
import { AppLink } from "@/types/appLink";
import { Puzzle } from "@/types/puzzles";
import { CustomCta } from "@/types/cta";
import { Device } from "@/types/device";
import { getLocalInstallationId, getSessionId } from "@/lib/logger";
import { getOsName } from "@/lib/device";

const v1Path = "/v1/app";
const v2Path = "/v2/app";
const v3Path = "/v3/app";

export interface Pagination<T> {
  data: T[];
  meta: {
    next_cursor: string | null;
  };
}

export enum EditionInclude {
  EndUserEditionData = "end_user_edition_data",
  EndUserSubscription = "end_user_subscription",
  Pages = "pages",
  Spreads = "spreads,spreads.first_page,spreads.second_page",
}

export enum EditionHitsInclude {
  EndUserEditionData = "edition.end_user_edition_data",
  EndUserSubscription = "edition.end_user_subscription",
  Pages = "edition.pages",
  Spreads = "edition.spreads,edition.spreads.first_page,edition.spreads.second_page",
}

export function getTitleEditions({
  cursor,
  include,
}: {
  cursor: string;
  include?: EditionInclude[];
}) {
  return client.get<Pagination<Edition>>(`${v1Path}/app-titles/editions`, {
    params: {
      cursor,
      per_page: 16,
      sort_key: "published_at",
      sort_dir: "desc",
      include: include && include.join(","),
    },
  });
}

export function getEdition(
  id: string | number,
  { include }: { include?: EditionInclude[] } = {}
) {
  return client.get<{ data: Edition }>(`${v1Path}/editions/${id}`, {
    params: {
      include: include && include.join(","),
    },
  });
}

export enum ArticleInclude {
  EditionTitle = "edition.title",
  FirstPage = "first_page",
  EndUserArticleData = "end_user_article_data",
  EndUserSubscription = "end_user_subscription",
}

export function getTitleArticles({
  cursor,
  sort,
  include,
}: {
  cursor: string;
  sort?: string;
  include?: ArticleInclude[];
}) {
  return client.get<Pagination<Article>>(`${v2Path}/app-titles/articles`, {
    params: {
      cursor,
      per_page: 16,
      sort: sort ?? "-published_at,+page_number",
      include: include && include.join(","),
    },
  });
}

export function getCustomCtas(timesUserOpenedAnEdition: number) {
  return client.post<{ data: CustomCta[] }>(
    `${v1Path}/actions/get-custom-ctas`,
    {
      times_user_opened_an_edition: timesUserOpenedAnEdition,
    }
  );
}

export function getPuzzles() {
  return client.get<{ data: Puzzle[] }>(`${v1Path}/puzzles`);
}

export function getPuzzle(id: string | number) {
  return client.get<{ data: Puzzle }>(`/v1/embed/puzzles/${id}`);
}

export function getArticle(
  id: string | number,
  {
    include,
  }: {
    include?: ArticleInclude[];
  } = {}
) {
  return client.get<{ data: Article }>(`${v1Path}/articles/${id}`, {
    params: {
      include: include && include.join(","),
    },
  });
}

export function getArticleTtsAudio(id: string | number) {
  return client.get<{ data: { tts_audio_url: string } }>(
    `${v1Path}/articles/${id}/tts-audio`
  );
}

export function getArticleRelatedArticles(
  articleId: string | number,
  { include }: { include?: ArticleInclude[] } = {}
) {
  return client.get<{ data: Article[]; status: boolean; message: string }>(
    `${v3Path}/articles/${articleId}/related-articles`,
    {
      params: {
        limit: 2,
        include: include && include.join(","),
      },
    }
  );
}

export function getEditionArticles({
  editionId,
  cursor,
  include,
}: {
  editionId: number;
  cursor: string;
  include?: ArticleInclude[];
}) {
  return client.get<Pagination<Article>>(
    `${v2Path}/editions/${editionId}/articles`,
    {
      params: {
        cursor,
        per_page: 16,
        sort_key: "page_number",
        sort_dir: "asc",
        include: include && include.join(","),
      },
    }
  );
}

export function getEditionArticlesAll(
  editionId: string | number,
  { include }: { include?: ArticleInclude[] } = {}
) {
  return client.get<{ data: Article[] }>(
    `${v1Path}/editions/${editionId}/articles/all`,
    {
      params: {
        sort_key: "first_page.number",
        sort_dir: "asc",
        include: include && include.join(","),
      },
    }
  );
}

export function postArticleShareLink(id: string | number) {
  return client.post<{ data: { url: string } }>(
    `${v1Path}/articles/${id}/share-link`
  );
}

export function postEditionShareLink(id: string | number) {
  return client.post<{ data: { url: string } }>(
    `${v1Path}/editions/${id}/share-link`
  );
}

export function getAppSettings() {
  return client.get<{ data: AppSettings }>(`${v1Path}/app-settings`);
}

export enum UserInclude {
  Subscriptions = "subscriptions",
  EndUserPropertyValues = "end_user_property_values",
  EndUserPropertyValuesEndUserProperty = "end_user_property_values.end_user_property",
}

export function getCurrentUser({ include }: { include?: UserInclude[] } = {}) {
  return client.get<{ data: EndUser }>(`${v1Path}/me`, {
    params: {
      include: include && include.join(","),
    },
  });
}

export function getTitleEditionsCurrentlyReading({
  cursor,
  include,
}: {
  cursor: string;
  include?: EditionInclude[];
}) {
  return client.get<Pagination<Edition>>(
    `${v1Path}/app-titles/editions/currently-reading`,
    {
      params: {
        cursor,
        per_page: 16,
        sort_key: "last_opened_at",
        sort_dir: "desc",
        include: include && include.join(","),
      },
    }
  );
}

export function getTitleEditionsBookmarked({
  cursor,
  include,
}: {
  cursor: string;
  include?: EditionInclude[];
}) {
  return client.get<Pagination<Edition>>(
    `${v1Path}/app-titles/editions/bookmarked`,
    {
      params: {
        cursor,
        per_page: 16,
        sort_key: "bookmark_modified_at",
        sort_dir: "desc",
        include: include && include.join(","),
      },
    }
  );
}

export function getTitleEditionHitsSearch({
  query,
  cursor,
  include,
}: {
  query: string;
  cursor: string;
  include?: EditionHitsInclude[];
}) {
  return client.get<Pagination<EditionHit>>(
    `${v1Path}/app-titles/edition-search-hits/search`,
    {
      params: {
        query,
        cursor,
        per_page: 8,
        sort_key: "match_score",
        sort_dir: "desc",
        include: include && include.join(","),
      },
    }
  );
}

export function getTitleArticlesBookmarked({
  cursor,
  include,
}: {
  cursor: string;
  include?: ArticleInclude[];
}) {
  return client.get<Pagination<Article>>(
    `${v1Path}/app-titles/articles/bookmarked`,
    {
      params: {
        cursor,
        per_page: 16,
        sort_key: "bookmark_modified_at",
        sort_dir: "desc",
        include: include && include.join(","),
      },
    }
  );
}

export function getTitleArticlesSearch({
  query,
  cursor,
  include,
}: {
  query: string;
  cursor: string;
  include?: ArticleInclude[];
}) {
  return client.get<Pagination<Article>>(
    `${v1Path}/app-titles/articles/search`,
    {
      params: {
        query,
        cursor,
        per_page: 8,
        sort_key: "match_score",
        sort_dir: "desc",
        include: include && include.join(","),
      },
    }
  );
}

export function getFaqs() {
  return client.get<{ data: Faq[] }>(`${v1Path}/faqs`);
}

export type ContactBody = {
  name: string;
  email: string;
  message: string;
  appSessionUuid?: string;
  appInstallationUuid?: string;
};

export function sendContactForm({
  name,
  email,
  message,
  appSessionUuid,
  appInstallationUuid,
}: ContactBody) {
  return client.post(`${v1Path}/contact-forms`, {
    name,
    email,
    message,
    build_number: __APP_VERSION__,
    user_agent: navigator.userAgent,
    app_session_uuid: appSessionUuid,
    app_installation_uuid: appInstallationUuid,
  });
}

export function updateUser({
  firstName,
  lastName,
  include,
}: {
  firstName: string;
  lastName: string;
  include?: UserInclude[];
}) {
  return client.put<{ data: EndUser }>(
    `${v1Path}/profile`,
    {
      firstname: firstName,
      lastname: lastName,
    },
    {
      params: {
        include: include && include.join(","),
      },
    }
  );
}

export function sendFeedbackHappy(happy: boolean) {
  return client.post(`${v1Path}/feedbacks/happy`, {
    happy,
    model_name: navigator.userAgent,
    system_version: navigator.platform,
    build_number: parseInt(__APP_VERSION__) || 0,
  });
}

export interface FeedbackBody {
  message: string;
  email: string | null;
}

export function sendFeedback({ message, email }: FeedbackBody) {
  return client.post(`${v1Path}/feedbacks`, {
    message,
    email,
    model_name: navigator.userAgent,
    system_version: navigator.platform,
    build_number: parseInt(__APP_VERSION__) || 0,
    app_installation_uuid: getLocalInstallationId(),
    app_session_uuid: getSessionId(),
  });
}

export function pushAppEvents(events: AppEventData[]) {
  return client.post(
    `${v1Path}/actions/push-app-events-idempotent`,
    events.map((e) => ({
      type: e.type,
      data: e.data,
    }))
  );
}

export function postEditionDownload(id: number | string) {
  return client.post<{ data: { pdf_url?: string } }>(
    `${v1Path}/editions/${id}/download`,
    {
      os_id: getLocalInstallationId(),
      os_name: getOsName(),
      device_type: "web",
    },
    {
      params: {
        download_type: "pdf",
      },
    }
  );
}

export function getAppLink(id: number | string) {
  return client.get<{ data: AppLink }>(`${v1Path}/app-links/${id}`, {
    params: {
      include: "linkable,linkable.linkable",
    },
  });
}

export function refreshMySubscriptions() {
  return client.post<Subscriptions>(
    `${v1Path}/actions/refresh-my-subscriptions`
  );
}

export function registerDevice(params: {
  os_id: string;
  os_name: string;
  device_type: string;
  brand: string | null;
  model: string | null;
}) {
  return client.post<{ data: Device }>(`${v1Path}/devices/register`, params);
}

export function getMyAppDevices() {
  return client.get<{ data: Device[] }>(`${v1Path}/me/app-devices`);
}

export function deleteDevice(id: number) {
  return client.delete(`${v1Path}/devices/${id}`);
}
